import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useThree } from '@react-three/fiber';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader.js';
import { PMREMGenerator, Color, LinearFilter, LinearMipmapLinearFilter } from 'three';

/**
 * Custom Environment-Komponente mit HDR-Support
 * Unterstützt sowohl lokale HDR-Dateien als auch Presets
 */
const EnvironmentWrapper = ({ preset, background, blur, customHDRUrl, backgroundColor, onError, randomizeEnvironment = false, isExporting = false, currentFrame = 0, frameInterval = 30, availableHDRs = [] }) => {
  // preset wird nicht mehr verwendet, bleibt aber für Kompatibilität
  const { gl, scene } = useThree();
  const pmremGeneratorRef = useRef(null);
  const [envMap, setEnvMap] = useState(null);
  const loaderRef = useRef(null);
  const abortControllerRef = useRef(null);
  const lastRandomizedFrameRef = useRef(-1);
  const currentHDRIndexRef = useRef(0);
  const hdrCacheRef = useRef(new Map()); // Cache für geladene HDR-Texturen

  // Initialisiere PMREMGenerator
  useEffect(() => {
    if (!pmremGeneratorRef.current) {
      pmremGeneratorRef.current = new PMREMGenerator(gl);
      pmremGeneratorRef.current.compileEquirectangularShader();
    }
    return () => {
      if (pmremGeneratorRef.current) {
        pmremGeneratorRef.current.dispose();
        pmremGeneratorRef.current = null;
      }
    };
  }, [gl]);

  // Preload alle HDR-Dateien wenn Randomisierung aktiviert ist (sowohl in Vorschau als auch beim Export)
  useEffect(() => {
    if (!randomizeEnvironment || availableHDRs.length === 0) return;
    if (!pmremGeneratorRef.current) return;

    console.log(`EnvironmentWrapper: Preloading ${availableHDRs.length} HDR files for randomization`);
    
    // Preload alle HDR-Dateien im Hintergrund
    availableHDRs.forEach((hdrName) => {
      const hdrPath = `${process.env.PUBLIC_URL}/hdri/${hdrName}`;
      
      // Nur preload wenn noch nicht im Cache
      if (!hdrCacheRef.current.has(hdrPath)) {
        const loader = getLoader(hdrPath);
        loader.load(
          hdrPath,
          (texture) => {
            try {
              if (pmremGeneratorRef.current) {
                // Setze Blur auf die Textur
                texture.userData.blur = blur;
                
                // Erstelle Environment-Map
                const envMap = pmremGeneratorRef.current.fromEquirectangular(texture).texture;
                
                // Wende Blur durch Anpassen der Textur-Filterung an
                if (blur > 0) {
                  envMap.minFilter = LinearFilter;
                  envMap.magFilter = LinearFilter;
                } else {
                  envMap.minFilter = LinearMipmapLinearFilter;
                  envMap.magFilter = LinearFilter;
                }
                
                hdrCacheRef.current.set(hdrPath, envMap);
                console.log(`EnvironmentWrapper: ✅ Preloaded HDR: ${hdrName} with blur ${blur}`);
              }
            } catch (error) {
              console.warn(`EnvironmentWrapper: Error preloading HDR ${hdrName}:`, error);
            }
            texture.dispose();
          },
          undefined,
          (error) => {
            console.warn(`EnvironmentWrapper: Failed to preload HDR ${hdrName}:`, error);
          }
        );
      }
    });
  }, [randomizeEnvironment, availableHDRs, blur]);

  // Setze Hintergrund IMMER - auch wenn keine HDR geladen wird
  useEffect(() => {
    // Setze Fallback-Hintergrund sofort
    if (backgroundColor) {
      scene.background = new Color(backgroundColor);
    }
  }, [scene, backgroundColor]);

  // Bestimme Loader basierend auf Dateiendung
  const getLoader = (url) => {
    const extension = url.toLowerCase().split('.').pop();
    if (extension === 'exr') {
      return new EXRLoader();
    } else {
      return new RGBELoader();
    }
  };
  
  const loadHDR = React.useCallback((url, isPreset = false, forceReload = false) => {
    if (!pmremGeneratorRef.current) {
      console.warn('EnvironmentWrapper: PMREMGenerator not ready');
      return;
    }

    // Prüfe Cache zuerst - wenn bereits geladen und nicht forciert, verwende sofort
    const cachedEnvMap = hdrCacheRef.current.get(url);
    if (cachedEnvMap && !forceReload) {
      console.log(`EnvironmentWrapper: ✅ Using cached HDR: ${url}`);
      // WICHTIG: Setze environment und background explizit, auch wenn aus Cache
      // Stelle sicher, dass die Werte wirklich gesetzt werden
      scene.environment = cachedEnvMap;
      if (background) {
        scene.background = cachedEnvMap;
      }
      setEnvMap(cachedEnvMap);
      // Stelle sicher, dass die Scene aktualisiert wird
      if (scene.needsUpdate !== undefined) {
        scene.needsUpdate = true;
      }
      return;
    }

    // Abbreche vorherigen Request falls vorhanden
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    let loaded = false;
    
      // Timeout für langsames Laden
      const timeoutId = setTimeout(() => {
        if (!loaded) {
          // Setze Fallback-Hintergrund wenn Timeout erreicht
          if (background && backgroundColor) {
            scene.background = new Color(backgroundColor);
          }
          abortControllerRef.current = null;
        }
    }, isPreset ? 2000 : 10000); // Längeres Timeout für große HDR-Dateien

    const loader = getLoader(url);
    loaderRef.current = loader;

    loader.load(
        url,
        (texture) => {
          if (loaded) return;
          clearTimeout(timeoutId);
          loaded = true;
          
          try {
          if (!pmremGeneratorRef.current) {
            console.warn('EnvironmentWrapper: PMREMGenerator disposed during load');
            texture.dispose();
            return;
          }
          
          // Wende Blur auf die Textur an
          // Setze die blur-Eigenschaft der Textur (0 = scharf, 1 = maximal unscharf)
          // Three.js verwendet diese Eigenschaft für die PMREM-Generierung
          texture.userData.blur = blur;
          
          // Erstelle Environment-Map
          // PMREMGenerator berücksichtigt automatisch die Textur-Eigenschaften
          const envMap = pmremGeneratorRef.current.fromEquirectangular(texture).texture;
          
          // Wende Blur durch Anpassen der Textur-Filterung an
          // Höherer Blur = mehr Unschärfe durch Filterung
          if (blur > 0) {
            // Verwende linear filtering für mehr Unschärfe
            envMap.minFilter = LinearFilter;
            envMap.magFilter = LinearFilter;
          } else {
            // Verwende nearest filtering für scharfe Texturen
            envMap.minFilter = LinearMipmapLinearFilter;
            envMap.magFilter = LinearFilter;
          }
          
          // Speichere im Cache für schnellen Zugriff
          hdrCacheRef.current.set(url, envMap);
          
          // WICHTIG: Setze environment und background explizit
          scene.environment = envMap;
          if (background) {
            scene.background = envMap;
          }
          setEnvMap(envMap);
          
          // Stelle sicher, dass die Scene aktualisiert wird
          scene.needsUpdate = true;
          
          const fileType = url.toLowerCase().endsWith('.exr') ? 'EXR' : 'HDR';
          console.log(`EnvironmentWrapper: ✅ Environment loaded and applied successfully: custom ${fileType} - ${url}`);
          } catch (error) {
          console.error('EnvironmentWrapper: Error processing environment texture:', error);
            // Fallback auf backgroundColor bei Fehler
            if (background && backgroundColor) {
              scene.background = new Color(backgroundColor);
            }
          }
          texture.dispose();
        },
        undefined,
        (error) => {
          clearTimeout(timeoutId);
          if (!loaded) {
          console.error('EnvironmentWrapper: Error loading environment:', error, url);
            // Setze Fallback-Hintergrund bei Fehler
            if (background && backgroundColor) {
              scene.background = new Color(backgroundColor);
            }
            abortControllerRef.current = null;
            if (onError) {
              onError(error);
            }
          }
        }
      );
  }, [background, backgroundColor, scene, onError, blur]);

  // Initiales Laden wenn Randomisierung aktiviert ist (sowohl in Vorschau als auch beim Export)
  useEffect(() => {
    if (!randomizeEnvironment || availableHDRs.length === 0) {
      // Reset beim Deaktivieren der Randomisierung
      if (!randomizeEnvironment) {
        lastRandomizedFrameRef.current = -1;
      }
      return;
    }
    if (!pmremGeneratorRef.current) {
      console.warn('EnvironmentWrapper: PMREMGenerator not ready for randomization');
      return;
    }

    // Lade das erste HDR aus der Liste wenn noch keines geladen wurde oder beim Start des Exports
    const shouldLoadInitial = (!isExporting && !envMap) || (isExporting && currentFrame === 0 && lastRandomizedFrameRef.current === -1);
    
    if (shouldLoadInitial) {
      const initialHDR = availableHDRs[0];
      if (initialHDR) {
        const hdrPath = `${process.env.PUBLIC_URL}/hdri/${initialHDR}`;
        currentHDRIndexRef.current = 0;
        lastRandomizedFrameRef.current = 0; // Setze initial frame group auf 0
        console.log(`EnvironmentWrapper: Loading initial HDR: ${initialHDR} (preview: ${!isExporting}, export: ${isExporting}, frame: ${currentFrame})`);
        loadHDR(hdrPath, false, false);
      }
    }
  }, [randomizeEnvironment, availableHDRs, loadHDR, isExporting, currentFrame, envMap]);

  // Randomisierung während Export - separate useEffect für Frame-basierte Änderungen
  useEffect(() => {
    // WICHTIG: Nur während Export und wenn Randomisierung aktiviert ist
    if (!isExporting || !randomizeEnvironment || availableHDRs.length === 0) {
      if (!isExporting && lastRandomizedFrameRef.current !== -1) {
        // Reset beim Beenden des Exports
        lastRandomizedFrameRef.current = -1;
      }
      return;
    }
    if (!pmremGeneratorRef.current) {
      console.warn('EnvironmentWrapper: PMREMGenerator not ready for randomization');
      return;
    }

    // Berechne Frame-Group: 
    // Frame 0-29: Gruppe 0 (bei interval=30)
    // Frame 30-59: Gruppe 1
    // Frame 60-89: Gruppe 2
    // etc.
    const frameGroup = Math.floor(currentFrame / frameInterval);
    const shouldChange = frameGroup !== lastRandomizedFrameRef.current;
    
    console.log(`EnvironmentWrapper: 📊 Frame ${currentFrame}, frameGroup: ${frameGroup}, lastGroup: ${lastRandomizedFrameRef.current}, shouldChange: ${shouldChange}, interval: ${frameInterval}, availableHDRs: ${availableHDRs.length}, isExporting: ${isExporting}, randomizeEnvironment: ${randomizeEnvironment}`);
    
    if (shouldChange) {
      const previousGroup = lastRandomizedFrameRef.current;
      lastRandomizedFrameRef.current = frameGroup;
      
      // Wähle zufällige HDR aus verfügbaren Dateien
      const randomIndex = Math.floor(Math.random() * availableHDRs.length);
      currentHDRIndexRef.current = randomIndex;
      const selectedHDR = availableHDRs[randomIndex];
      const hdrPath = `${process.env.PUBLIC_URL}/hdri/${selectedHDR}`;
      
      console.log(`EnvironmentWrapper: 🔄🔄🔄 CHANGING HDR from group ${previousGroup} to group ${frameGroup} - Loading: ${selectedHDR} at frame ${currentFrame} (interval: ${frameInterval})`);
      // Force reload um sicherzustellen, dass die Scene aktualisiert wird
      loadHDR(hdrPath, false, true);
    } else if (currentFrame === 0 && lastRandomizedFrameRef.current === -1) {
      // Initiales Laden beim ersten Frame
      lastRandomizedFrameRef.current = 0;
      const initialHDR = availableHDRs[0];
      if (initialHDR) {
        const hdrPath = `${process.env.PUBLIC_URL}/hdri/${initialHDR}`;
        currentHDRIndexRef.current = 0;
        console.log(`EnvironmentWrapper: 🎬 Initial HDR at frame 0: ${initialHDR}`);
        loadHDR(hdrPath, false, true);
      }
    } else {
      console.log(`EnvironmentWrapper: ⏸️  Keeping same HDR (frame ${currentFrame}, group ${frameGroup}, lastGroup: ${lastRandomizedFrameRef.current})`);
    }
  }, [currentFrame, frameInterval, isExporting, randomizeEnvironment, availableHDRs, loadHDR]);

  // Standard-Laden (ohne Randomisierung) - nur lokale HDR-Dateien
  useEffect(() => {
    if (!pmremGeneratorRef.current) return;
    if (isExporting && randomizeEnvironment) return; // Überspringe wenn Randomisierung aktiv ist

    // Nur Custom HDR/EXR URL verwenden (lokale Dateien)
    const hdrUrl = customHDRUrl || null;
    
    if (hdrUrl) {
      // Wenn Custom HDR URL vorhanden und keine Randomisierung, lade diese
      loadHDR(hdrUrl, false, false);
    }
    // Wenn keine Custom HDR: Verwende Fallback-Hintergrund (wird bereits im anderen useEffect gesetzt)

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [customHDRUrl, loadHDR, isExporting, randomizeEnvironment]);

  // Cleanup beim Unmount
  useEffect(() => {
    return () => {
      if (envMap) {
        envMap.dispose();
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [envMap]);

  // Rendere nichts - die Environment-Map wird direkt auf die Scene angewendet
  return null;
};

export default EnvironmentWrapper;
